import { TableItemFragment } from '@client/graphql/types/graphql.ts';
import { objectSortByString } from '@client/module/utils/sort.ts';
import { useGenerateMultipleQRCodes } from '@client/page/management/table-group/table-group-list/logics/use-generate-multiple-qr-code';
import TableEditBatch from '@client/page/management/table/table-edit-batch/table-edit-batch.tsx';
import { useDeleteTableBatch } from '@client/page/management/table/table-list/logic/use-delete-table-batch.ts';
import { ButtonSize } from '@packages/ui/button/button-size.ts';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import { useDialog } from '@packages/ui/modal/use-dialog.tsx';
import SelectionTableRow from '@packages/ui/table/selection-table-row.tsx';
import { SelectionTableDataSource, useSelectionTableDataSource } from '@packages/ui/table/selection-table-type.ts';
import SelectionTable from '@packages/ui/table/selection-table.tsx';
import TableHeaderColumn from '@packages/ui/table/table-header-column.tsx';
import TableRowColumn from '@packages/ui/table/table-row-cell.tsx';
import { TableCellAlignment } from '@packages/ui/table/table-type.ts';
import { useTranslate } from '@tolgee/react';
import { useCompanyService } from 'core/src/company/company-hook';
import { saveAs } from 'file-saver';
import { toJpeg } from 'html-to-image';
import JSZip from 'jszip';
import { useRef, useState } from 'react';
import { useModalManagerContext } from 'ui/src/modal/modal-manager-context';
import { useNotificationCenter } from 'ui/src/notification/notification-center';
import Spinner from 'ui/src/spinner';
import { SpinnerSize } from 'ui/src/spinner-size';
import UpdateTableModal from './update-table-modal';

export default function TableList(props: { loading: boolean; data: TableItemFragment[] }) {
  const company = useCompanyService();
  const dialog = useDialog();
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();

  const [update] = useGenerateMultipleQRCodes();
  const selectionDataSource = useSelectionTableDataSource<TableItemFragment>(props.data);
  const qrRef = useRef<HTMLDivElement>(null);

  const { selected, editingMode } = selectionDataSource;
  const batchDelete = useDeleteTableBatch(Array.from(selected));
  const [qrLoading, setQrLoading] = useState<boolean>(false);

  if (props.data.length == 0) {
    return <p className="p-4 text-gray-600">{t('table-list.empty.data.message')}</p>;
  }

  // Tạo template QR code với thiết kế đẹp hơn
  const createQRCodeTemplate = (qrCode: string, tableName: string): string => {
    return `
      <div class="w-64 h-80 bg-gradient-to-b from-blue-50 to-blue-100 border-4 border-blue-500 rounded-lg flex flex-col items-center justify-center
       p-4 shadow-lg">
        <div class="text-center mb-2">
          <h2 class="text-xl font-bold text-blue-800">Table ${tableName}</h2>
        </div>
        <div class="bg-white p-2 rounded-lg shadow-md overflow-hidden">
          <img src="${qrCode}" class="w-full h-full" alt="QR Code for ${tableName}" />
        </div>
        <div class="mt-3 text-center justify-center">
          <p class="text-lg font-bold text-blue-700">${company.company?.name ?? ''}</p>
        </div>
      </div>
    `;
  };

  // Chuyển đổi HTML thành hình ảnh với chất lượng cao hơn
  const convertHtmlToImage = async (element: HTMLDivElement | null): Promise<string> => {
    if (!element) return '';

    // Không cần set innerHTML ở đây nữa vì đã set trong vòng lặp xử lý
    const dataUrl = await toJpeg(element, {
      width: 256,
      height: 320,
      pixelRatio: 3, // Tăng chất lượng ảnh lên cao hơn
      cacheBust: true,
      quality: 0.95, // Tăng chất lượng JPEG
      backgroundColor: '#ffffff', // Đảm bảo nền trắng
    });

    return dataUrl.split(',')[1] ?? '';
  };

  const handleGenerateMultipleDecoratorQR = async () => {
    try {
      setQrLoading(true);
      const selectedTables = Array.from(selected);

      if (selectedTables.length === 0) {
        showAlert({
          status: 'warning',
          title: t('qr-gen.no-selection.title', 'No Tables Selected'),
          message: t('qr-gen.no-selection.message', 'Please select at least one table to generate QR codes'),
        });
        return;
      }

      if (!qrRef.current) {
        throw new Error('QR reference element not found');
      }

      // Lấy dữ liệu QR code từ API
      const response = await update({
        variables: {
          tableIds: selectedTables.map((table) => table.id),
        },
      });

      const results = response.data?.generateMultipleQRCodes ?? [];

      if (results.length === 0) {
        throw new Error('No QR codes were generated');
      }

      const zip = new JSZip();

      // Xử lý tuần tự từng QR code và thêm vào file zip
      for (const result of results) {
        const { tableId, qrCode } = result;
        const table = selectedTables.find((item) => item.id === tableId);

        if (!table || !qrCode) continue;

        const template = createQRCodeTemplate(qrCode, table.name);
        // Đảm bảo DOM được cập nhật trước khi chuyển đổi thành hình ảnh
        qrRef.current.innerHTML = template;

        // Thêm một khoảng thời gian nhỏ để đảm bảo DOM được render
        await new Promise((resolve) => setTimeout(resolve, 100));

        const base64Data = await convertHtmlToImage(qrRef.current);
        zip.file(`table-${table.name.replace(/\s+/g, '-')}-qrcode.png`, base64Data, { base64: true });
      }

      // Tạo và tải xuống file zip
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `table-qrcodes-${new Date().toISOString().slice(0, 10)}.zip`);

      showAlert({
        status: 'success',
        title: t('qr-gen.success.title', 'QR Codes Generated'),
        message: t('qr-gen.success.message', `Successfully generated ${results.length.toString()} QR codes`),
      });
      setQrLoading(false);
    } catch (error) {
      showAlert({
        status: 'error',
        title: t('qr-gen.header', 'Error'),
        message: t('qr-gen.message', 'An error occurred while generating QR codes'),
      });
    }
  };

  return (
    <>
      <SelectionTable
        dataSource={selectionDataSource}
        loading={props.loading}
        data={props.data.sort(objectSortByString((table) => table.name))}
        build={(table) => {
          return <Row key={table.id} table={table} dataSource={selectionDataSource} />;
        }}
      >
        <TableHeaderColumn alignment={TableCellAlignment.Left}>
          {editingMode ? (
            <div className="space-x-2">
              <SecondaryButton
                buttonSize={ButtonSize.semiSmall}
                onClick={() => {
                  dialog.showComponent((onClose) => <TableEditBatch tables={Array.from(selected)} onClose={onClose} />);
                }}
              >
                {t('table-list.bulk-edit')}
              </SecondaryButton>
              <SecondaryButton
                buttonSize={ButtonSize.semiSmall}
                onClick={() => {
                  batchDelete();
                }}
              >
                {t('common.delete')}
              </SecondaryButton>

              <SecondaryButton
                buttonSize={ButtonSize.semiSmall}
                onClick={() => {
                  void handleGenerateMultipleDecoratorQR();
                }}
              >
                {qrLoading ? <Spinner size={SpinnerSize.Small} /> : <span> {t('common.generate-qr-code')}</span>}
              </SecondaryButton>
            </div>
          ) : (
            t('common.name')
          )}
        </TableHeaderColumn>
        <TableHeaderColumn alignment={TableCellAlignment.Right}>{t('common.seats')}</TableHeaderColumn>
      </SelectionTable>

      <div className="hidden">
        <div ref={qrRef} className="" />
      </div>
    </>
  );
}

function Row(props: { table: TableItemFragment; dataSource: SelectionTableDataSource<TableItemFragment> }) {
  const dialogManager = useModalManagerContext();
  const updateModal = () => {
    dialogManager.showDialog((onclose) => (
      <UpdateTableModal onClose={onclose} tableGroupId={props.table.tableGroupId} tableId={props.table.id} />
    ));
  };

  return (
    <SelectionTableRow item={props.table} dataSource={props.dataSource}>
      <TableRowColumn className="py-4">
        <p onClick={updateModal}>{props.table.name}</p>
      </TableRowColumn>
      <TableRowColumn alignment={TableCellAlignment.Right}>
        <p onClick={updateModal}>{props.table.seats}</p>
      </TableRowColumn>
    </SelectionTableRow>
  );
}
